import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { metadados, canonical } from '../seo';
import { site } from '../config';

/**
 * Mantém o <head> sincronizado com a rota atual.
 *
 * O HTML estático do build já sai com as tags certas (scripts/prerender.js) —
 * isto aqui existe para a navegação feita dentro do site, sem recarregar
 * a página, continuar coerente para o usuário e para os robôs.
 *
 * Escrevemos direto no DOM em vez de renderizar as tags porque elas ficam
 * no <head>, fora da árvore do React, e assim não há risco de conflito com
 * o HTML pré-renderizado durante a hidratação.
 */
export default function Seo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const dados = metadados(pathname);
    const url = canonical(dados.caminho);

    document.title = dados.titulo;

    definirMeta('name', 'description', dados.descricao);
    definirMeta('name', 'robots', dados.semIndexacao ? 'noindex, follow' : 'index, follow');
    definirMeta('property', 'og:title', dados.titulo);
    definirMeta('property', 'og:description', dados.descricao);
    definirMeta('property', 'og:url', url);
    definirMeta('property', 'og:image', dados.imagem);
    definirMeta('property', 'og:type', pathname === '/' ? 'website' : 'article');
    definirMeta('property', 'og:site_name', `${site.nome} — Advocacia Criminal`);
    definirMeta('name', 'twitter:card', 'summary_large_image');

    definirLink('canonical', url);
    definirJsonLd(dados.jsonLd);
  }, [pathname]);

  return null;
}

function definirMeta(atributo, nome, conteudo) {
  if (!conteudo) return;

  let tag = document.head.querySelector(`meta[${atributo}="${nome}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(atributo, nome);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', conteudo);
}

function definirLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

function definirJsonLd(dados) {
  document.head
    .querySelectorAll('script[type="application/ld+json"]')
    .forEach((tag) => tag.remove());

  if (!dados) return;

  const tag = document.createElement('script');
  tag.type = 'application/ld+json';
  tag.textContent = JSON.stringify(dados);
  document.head.appendChild(tag);
}
